import * as THREE from 'three';
import { LineSegmentsBVH } from 'three-mesh-bvh';
import type {
    VisibleModelEdgeProjectionHit,
    VisibleModelEdgeProjectionPickInput,
    VisibleModelEdgeProjectionRecord,
} from '../../../types/sketch/VisibleModelEdgeProjection';
import { setRaycasterFirstHitOnly } from '../../raycast-acceleration';

/**
 * 可见模型边投影线 BVH 索引。
 *
 * 职责：
 * 1. 将可见投影子线段压平为单个 `LineSegments` 几何；
 * 2. 以 indirect 模式构建 `LineSegmentsBVH`，保持图元到拓扑边的稳定映射；
 * 3. 将鼠标屏幕像素位置转换为草图平面世界阈值并返回权威拓扑边命中。
 *
 * 索引对象不进入场景渲染树，只承担交互查询职责。
 */
export class VisibleModelEdgeProjectionIndex {
    /** 复用的投影线射线查询器。 */
    private readonly raycaster = new THREE.Raycaster();

    /** 复用的主指针 NDC。 */
    private readonly pointerNdc = new THREE.Vector2();

    /** 复用的像素阈值偏移 NDC。 */
    private readonly offsetNdc = new THREE.Vector2();

    /** 复用的主指针阈值换算射线。 */
    private readonly pointerThresholdRaycaster = new THREE.Raycaster();

    /** 复用的偏移指针阈值换算射线。 */
    private readonly offsetThresholdRaycaster = new THREE.Raycaster();

    /** 复用的 LineSegmentsBVH 候选命中数组。 */
    private readonly lineHits: THREE.Intersection[] = [];

    /** 投影线图元到权威拓扑记录的映射。 */
    private readonly primitiveRecords: VisibleModelEdgeProjectionRecord[] = [];

    /** 投影线图元在所属记录中的子段序号。 */
    private readonly primitiveSegmentIndices: number[] = [];

    /** 当前 BVH 查询对象。 */
    private lineSegments: THREE.LineSegments | null = null;

    /** 当前投影线专用的 indirect LineSegmentsBVH。 */
    private lineSegmentsBvh: LineSegmentsBVH | null = null;

    /** 复用的主指针平面交点。 */
    private readonly pointerPlanePoint = new THREE.Vector3();

    /** 复用的偏移指针平面交点。 */
    private readonly offsetPlanePoint = new THREE.Vector3();

    /**
     * 用新的可见投影记录重建线 BVH。
     *
     * @param records 当前草图会话的可见投影记录。
     */
    public rebuild(records: readonly VisibleModelEdgeProjectionRecord[]): void {
        this.disposeGeometry();
        this.primitiveRecords.length = 0;
        this.primitiveSegmentIndices.length = 0;

        const positions: number[] = [];
        records.forEach((record) => {
            record.visibleSegments.forEach((segment, segmentIndex) => {
                positions.push(
                    segment.start.x,
                    segment.start.y,
                    segment.start.z,
                    segment.end.x,
                    segment.end.y,
                    segment.end.z,
                );
                this.primitiveRecords.push(record);
                this.primitiveSegmentIndices.push(segmentIndex);
            });
        });
        if (positions.length === 0) {
            return;
        }

        const geometry = new THREE.BufferGeometry();
        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        this.lineSegments = new THREE.LineSegments(geometry, new THREE.LineBasicMaterial());

        /*
         * 必须使用 indirect 模式保留图元原始顺序，否则 BVH 构建重排几何后，图元序号将无法
         * 稳定映射回权威拓扑边。索引对象不进入场景，所以直接调用 BVH 查询而不覆写对象 raycast。
         */
        this.lineSegmentsBvh = new LineSegmentsBVH(geometry, { indirect: true });
        // BVH 只负责快速收集阈值内候选，最终仍需在局部候选中选择离指针射线最近的投影子段。
        setRaycasterFirstHitOnly(this.raycaster, false);
    }

    /**
     * 查询当前鼠标下最近的可见投影子线段。
     *
     * @param input 屏幕位置、相机、视口和草图平面。
     * @returns 命中的权威拓扑边记录；未命中时返回 null。
     */
    public pick(input: VisibleModelEdgeProjectionPickInput): VisibleModelEdgeProjectionHit | null {
        if (
            !this.lineSegments
            || !this.lineSegmentsBvh
            || input.viewportSize.x <= 0
            || input.viewportSize.y <= 0
        ) {
            return null;
        }

        this.pointerNdc.set(
            (input.screenPosition.x / input.viewportSize.x) * 2 - 1,
            1 - (input.screenPosition.y / input.viewportSize.y) * 2,
        );
        this.raycaster.setFromCamera(this.pointerNdc, input.camera);
        this.raycaster.params.Line.threshold = this.resolveWorldThreshold(input, this.pointerNdc);
        this.lineHits.length = 0;
        this.lineSegmentsBvh.raycastObject3D(this.lineSegments, this.raycaster, this.lineHits);
        let hit: THREE.Intersection | null = null;
        let bestDistance = Number.POSITIVE_INFINITY;
        for (const candidate of this.lineHits) {
            const candidateDistance = this.raycaster.ray.distanceSqToPoint(candidate.point);
            if (candidateDistance < bestDistance) {
                bestDistance = candidateDistance;
                hit = candidate;
            }
        }
        if (!hit) {
            return null;
        }

        // LineSegmentsBVH 的 index 是命中图元首顶点索引，每个图元固定占用两个顶点。
        const vertexIndex = Reflect.get(hit, 'index');
        if (typeof vertexIndex !== 'number') {
            return null;
        }
        const primitiveIndex = Math.floor(vertexIndex / 2);
        const record = this.primitiveRecords[primitiveIndex] ?? null;
        const segmentIndex = this.primitiveSegmentIndices[primitiveIndex];
        if (!record || typeof segmentIndex !== 'number') {
            return null;
        }

        return {
            record,
            segmentIndex,
        };
    }

    /**
     * 释放索引几何、材质与 BVH。
     */
    public dispose(): void {
        this.disposeGeometry();
        this.primitiveRecords.length = 0;
        this.primitiveSegmentIndices.length = 0;
    }

    /**
     * 将屏幕像素拾取半径换算为草图平面上的世界距离。
     *
     * @param input 拾取输入。
     * @param pointerNdc 主指针标准化设备坐标。
     * @returns 可供 LineSegmentsBVH 使用的世界距离阈值。
     */
    private resolveWorldThreshold(
        input: VisibleModelEdgeProjectionPickInput,
        pointerNdc: THREE.Vector2,
    ): number {
        this.offsetNdc.set(
            pointerNdc.x + (input.thresholdPx / input.viewportSize.x) * 2,
            pointerNdc.y,
        );
        this.pointerThresholdRaycaster.setFromCamera(pointerNdc, input.camera);
        this.offsetThresholdRaycaster.setFromCamera(this.offsetNdc, input.camera);

        const pointerIntersection = this.pointerThresholdRaycaster.ray.intersectPlane(
            input.referencePlane,
            this.pointerPlanePoint,
        );
        const offsetIntersection = this.offsetThresholdRaycaster.ray.intersectPlane(
            input.referencePlane,
            this.offsetPlanePoint,
        );
        if (!pointerIntersection || !offsetIntersection) {
            return 1e-6;
        }
        return Math.max(1e-9, pointerIntersection.distanceTo(offsetIntersection));
    }

    /**
     * 释放当前 LineSegments 查询对象持有的全部资源。
     */
    private disposeGeometry(): void {
        if (!this.lineSegments) {
            return;
        }
        this.lineSegments.geometry.dispose();
        const material = this.lineSegments.material;
        if (Array.isArray(material)) {
            material.forEach((item) => item.dispose());
        } else {
            material.dispose();
        }
        this.lineSegments = null;
        this.lineSegmentsBvh = null;
    }
}
